import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';
import { CURRENCY } from '../config';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December'];

const REPORTS = [
  { key: 'fee_collection', label: 'Fee Collection Report', icon: 'fa-credit-card',
    roles: ['admin', 'accountant', 'principal', 'superintendent'] },
  { key: 'defaulters', label: 'Defaulters / Pending Fees', icon: 'fa-exclamation-triangle',
    roles: ['admin', 'accountant', 'principal', 'superintendent'] },
  { key: 'attendance', label: 'Class Attendance Report', icon: 'fa-clipboard-check',
    roles: ['admin', 'accountant', 'principal', 'superintendent', 'teacher'] },
  { key: 'grades', label: 'Student Progress / Grades', icon: 'fa-star',
    roles: ['admin', 'principal', 'superintendent', 'teacher'] },
  { key: 'revenue', label: 'Campus Revenue Summary', icon: 'fa-chart-line',
    roles: ['admin', 'superintendent', 'principal'] },
  { key: 'leaves', label: 'Teacher Leave Summary', icon: 'fa-calendar-times',
    roles: ['admin', 'principal', 'superintendent'] },
  { key: 'salary', label: 'Salary Report', icon: 'fa-money-bill-wave',
    roles: ['admin', 'accountant'] },
];

export default function Reports() {
  const { user } = useAuth();
  const role = user?.role;

  const now = new Date();
  const [reportKey, setReportKey] = useState('fee_collection');
  const [loading, setLoading] = useState(false);

  const [data, setData] = useState([]);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [fees, setFees] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [grades, setGrades] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [salaries, setSalaries] = useState([]);

  // Filters
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [filterCampus, setFilterCampus] = useState('all');
  const [filterClass, setFilterClass] = useState('all');

  const visibleReports = REPORTS.filter(r => r.roles.includes(role));

  useEffect(() => {
    if (!visibleReports.find(r => r.key === reportKey)) {
      setReportKey(visibleReports[0]?.key);
    }
  }, [role]);

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { buildReport(); }, [reportKey, month, year, filterCampus, filterClass, fees, students, attendance, grades, leaves, salaries]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [stRes, clRes, camRes, feeRes, attRes, grRes, salRes] = await Promise.all([
        api.get('/students/'),
        api.get('/classes/'),
        api.get('/campuses/'),
        api.get('/fees/'),
        api.get('/attendance/'),
        api.get('/grades/'),
        api.get('/salaries/').catch(() => ({ data: [] })),
      ]);
      setStudents(stRes.data || []);
      setClasses(clRes.data || []);
      setCampuses(camRes.data || []);
      setFees(feeRes.data || []);
      setAttendance(attRes.data || []);
      setGrades(grRes.data || []);
      setSalaries(salRes.data || []);

      // Leaves only for principal/admin
      if (['admin', 'principal', 'superintendent'].includes(role)) {
        try {
          const lvRes = await api.get('/teacher-leaves/').catch(() => ({ data: [] }));
          setLeaves(lvRes.data || []);
        } catch (e) { setLeaves([]); }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // ===== BUILD REPORT DATA =====
  const buildReport = () => {
    let rows = [];

    if (reportKey === 'fee_collection') {
      let feeList = fees.filter(f => {
        if (!f.due_date) return false;
        const d = new Date(f.due_date);
        return d.getMonth() + 1 === month && d.getFullYear() === year;
      });
      if (filterCampus !== 'all') {
        const campus = campuses.find(c => c.id === parseInt(filterCampus));
        feeList = feeList.filter(f => {
          const student = students.find(s => s.id === f.student_id);
          return student?.campus_name === campus?.name;
        });
      }
      if (filterClass !== 'all') {
        feeList = feeList.filter(f => {
          const student = students.find(s => s.id === f.student_id);
          return student?.class_name === filterClass;
        });
      }
      rows = feeList.map(f => {
        const student = students.find(s => s.id === f.student_id);
        return {
          Student: student?.student_name || `#${f.student_id}`,
          Class: student?.class_name || '—',
          Type: f.fee_type,
          Base: f.base_amount ? `${CURRENCY}${f.base_amount}` : '—',
          Discount: f.discount_percentage > 0 ? `${f.discount_percentage}%` : '—',
          Final: `${CURRENCY}${f.amount}`,
          Paid: `${CURRENCY}${f.paid_amount || 0}`,
          Balance: `${CURRENCY}${((f.amount || 0) - (f.paid_amount || 0)).toFixed(2)}`,
          Status: f.status,
        };
      });
    }

    else if (reportKey === 'defaulters') {
      let feeList = fees.filter(f => f.status !== 'Paid');
      if (filterCampus !== 'all') {
        const campus = campuses.find(c => c.id === parseInt(filterCampus));
        feeList = feeList.filter(f => {
          const student = students.find(s => s.id === f.student_id);
          return student?.campus_name === campus?.name;
        });
      }
      rows = feeList.map(f => {
        const student = students.find(s => s.id === f.student_id);
        const balance = (f.amount || 0) - (f.paid_amount || 0);
        const isOverdue = new Date(f.due_date) < new Date();
        return {
          Student: student?.student_name || `#${f.student_id}`,
          Class: student?.class_name || '—',
          Guardian: student?.guardian_name || '—',
          Phone: student?.guardian_phone || '—',
          Fee: f.fee_type,
          Due: `${CURRENCY}${f.amount}`,
          Balance: `${CURRENCY}${balance.toFixed(2)}`,
          'Due Date': f.due_date,
          Status: isOverdue ? '⚠️ Overdue' : 'Pending',
        };
      });
    }

    else if (reportKey === 'attendance') {
      // Group attendance by student + class
      const summary = {};
      attendance.forEach(a => {
        const key = a.student_id;
        if (!summary[key]) summary[key] = { present: 0, absent: 0, late: 0, total: 0 };
        summary[key].total++;
        if (a.status === 'Present') summary[key].present++;
        else if (a.status === 'Absent') summary[key].absent++;
        else if (a.status === 'Late') summary[key].late++;
      });
      rows = Object.keys(summary).map(sid => {
        const student = students.find(s => s.id === parseInt(sid));
        const s = summary[sid];
        const pct = s.total ? ((s.present / s.total) * 100).toFixed(1) : 0;
        return {
          Student: student?.student_name || `#${sid}`,
          Class: student?.class_name || '—',
          Present: s.present,
          Absent: s.absent,
          Late: s.late,
          Total: s.total,
          Rate: `${pct}%`,
        };
      });
    }

    else if (reportKey === 'grades') {
      let gradeList = grades;
      if (filterClass !== 'all') {
        gradeList = gradeList.filter(g => {
          const student = students.find(s => s.id === g.student_id);
          return student?.class_name === filterClass;
        });
      }
      rows = gradeList.map(g => {
        const student = students.find(s => s.id === g.student_id);
        return {
          Student: student?.student_name || `#${g.student_id}`,
          Class: student?.class_name || '—',
          Subject: g.subject,
          Exam: g.exam_name,
          Marks: `${g.marks_obtained ?? 0}/${g.total_marks ?? 100}`,
          Percentage: `${g.percentage ?? 0}%`,
          Grade: g.grade || '—',
        };
      });
    }

    else if (reportKey === 'revenue') {
      rows = campuses.map(c => {
        const campusStudents = students.filter(s => s.campus_name === c.name);
        const campusFees = fees.filter(f => {
          const student = students.find(s => s.id === f.student_id);
          return student?.campus_name === c.name;
        });
        const total = campusFees.reduce((s, f) => s + (f.amount || 0), 0);
        const paid = campusFees.reduce((s, f) => s + (f.paid_amount || 0), 0);
        return {
          Campus: c.name,
          Students: campusStudents.length,
          'Total Billed': `${CURRENCY}${total}`,
          Collected: `${CURRENCY}${paid}`,
          Pending: `${CURRENCY}${(total - paid).toFixed(2)}`,
        };
      });
    }

    else if (reportKey === 'leaves') {
      rows = leaves.map(l => {
        return {
          Teacher: l.teacher_name || `#${l.teacher_id}`,
          'Start Date': l.start_date,
          'End Date': l.end_date,
          Reason: l.reason || '—',
          Status: l.status,
          Substitute: l.substitute_teacher_name || '—',
        };
      });
    }

    else if (reportKey === 'salary') {
      let salaryList = salaries.filter(s => s.month === month && s.year === year);
      rows = salaryList.map(s => {
        const adj = s.adjustments || [];
        const adds = adj.filter(a => a.type === 'add').reduce((sum, a) => sum + (a.value || 0), 0);
        const ded = adj.filter(a => a.type === 'deduct').reduce((sum, a) => sum + (a.value || 0), 0);
        const net = (s.amount || 0) + adds - ded;
        return {
          Name: s.person_name,
          Type: s.person_type,
          Basic: `${CURRENCY}${s.amount}`,
          Additions: adds > 0 ? `+${CURRENCY}${adds}` : '—',
          Deductions: ded > 0 ? `−${CURRENCY}${ded}` : '—',
          Net: `${CURRENCY}${net}`,
          Status: s.status,
        };
      });
    }

    setData(rows);
  };

  // ===== TOTALS PER REPORT TYPE =====
  const totals = (() => {
    if (reportKey === 'fee_collection') {
      const total = data.reduce((s, r) => s + parseFloat((r.Final || '0').replace(/[^\d.]/g, '')) || 0, 0);
      const paid = data.reduce((s, r) => s + parseFloat((r.Paid || '0').replace(/[^\d.]/g, '')) || 0, 0);
      return [
        { label: 'Total Billed', value: `${CURRENCY}${total.toFixed(2)}` },
        { label: 'Collected', value: `${CURRENCY}${paid.toFixed(2)}` },
        { label: 'Pending', value: `${CURRENCY}${(total - paid).toFixed(2)}` },
      ];
    }
    if (reportKey === 'defaulters') {
      const total = data.reduce((s, r) => s + parseFloat((r.Balance || '0').replace(/[^\d.]/g, '')) || 0, 0);
      return [
        { label: 'Defaulters Count', value: data.length },
        { label: 'Total Outstanding', value: `${CURRENCY}${total.toFixed(2)}` },
      ];
    }
    if (reportKey === 'attendance') {
      const totalPresent = data.reduce((s, r) => s + (r.Present || 0), 0);
      const totalAbsent = data.reduce((s, r) => s + (r.Absent || 0), 0);
      const totalLate = data.reduce((s, r) => s + (r.Late || 0), 0);
      return [
        { label: 'Total Present', value: totalPresent },
        { label: 'Total Absent', value: totalAbsent },
        { label: 'Total Late', value: totalLate },
      ];
    }
    if (reportKey === 'revenue') {
      const collected = data.reduce((s, r) => s + parseFloat((r.Collected || '0').replace(/[^\d.]/g, '')) || 0, 0);
      const pending = data.reduce((s, r) => s + parseFloat((r.Pending || '0').replace(/[^\d.]/g, '')) || 0, 0);
      return [
        { label: 'Total Collected', value: `${CURRENCY}${collected.toFixed(2)}` },
        { label: 'Total Pending', value: `${CURRENCY}${pending.toFixed(2)}` },
      ];
    }
    if (reportKey === 'salary') {
      const net = data.reduce((s, r) => s + parseFloat((r.Net || '0').replace(/[^\d.]/g, '')) || 0, 0);
      return [
        { label: 'Total Net Payable', value: `${CURRENCY}${net.toFixed(2)}` },
        { label: 'Records', value: data.length },
      ];
    }
    return [];
  })();

  // ===== COLUMNS =====
  const columns = data.length > 0 ? Object.keys(data[0]) : [];

  const currentReport = REPORTS.find(r => r.key === reportKey);

  const handlePrint = () => window.print();

  const handleExportCSV = () => {
    if (data.length === 0) {
      alert('No data to export');
      return;
    }
    const headers = Object.keys(data[0]);
    const csv = [
      headers.join(','),
      ...data.map(row => headers.map(h => `"${String(row[h] ?? '').replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${reportKey}_${year}_${month}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        <h2 className="text-xl font-bold">📊 Reports</h2>
        <div className="flex gap-2">
          <button
            onClick={handleExportCSV}
            className="px-3 py-2 text-sm bg-green-100 text-green-700 rounded hover:bg-green-200"
            disabled={data.length === 0}
          >
            <i className="fas fa-file-csv mr-1"></i> Export CSV
          </button>
          <button
            onClick={handlePrint}
            className="px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
            disabled={data.length === 0}
          >
            <i class="fas fa-print mr-1"></i> Print
          </button>
        </div>
      </div>

      {/* REPORT TYPE SELECTOR */}
      <div className="bg-white p-4 rounded shadow mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select Report Type
        </label>
        <div className="flex flex-wrap gap-2">
          {visibleReports.map(r => (
            <button
              key={r.key}
              onClick={() => setReportKey(r.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium border transition ${
                reportKey === r.key
                  ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                  : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
              }`}
            >
              <i className={`fas ${r.icon} mr-2`}></i>
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* FILTERS */}
      <div className="bg-white p-3 rounded shadow mb-4 flex flex-wrap gap-3 items-center">
        {/* Month & Year */}
        {(reportKey === 'fee_collection' || reportKey === 'salary') && (
          <>
            <select className="p-2 border rounded text-sm"
              value={month}
              onChange={(e) => setMonth(parseInt(e.target.value))}>
              {MONTHS.map((m, i) => (
                <option key={i + 1} value={i + 1}>{m}</option>
              ))}
            </select>
            <select className="p-2 border rounded text-sm"
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value))}>
              {[2024, 2025, 2026].map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </>
        )}

        {/* Campus Filter */}
        {['fee_collection', 'defaulters', 'revenue'].includes(reportKey) && filterCampus !== undefined && (
          <select className="p-2 border rounded text-sm"
            value={filterCampus}
            onChange={(e) => setFilterCampus(e.target.value)}>
            <option value="all">All Campuses</option>
            {campuses.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        )}

        {/* Class Filter */}
        {['fee_collection', 'grades', 'attendance'].includes(reportKey) && (
          <select className="p-2 border rounded text-sm"
            value={filterClass}
            onChange={(e) => setFilterClass(e.target.value)}>
            <option value="all">All Classes</option>
            {classes.map(c => (
              <option key={c.id} value={c.name}>{c.name}</option>
            ))}
          </select>
        )}

        <span className="ml-auto text-sm text-gray-500">
          {data.length} record{data.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* TOTALS */}
      {totals.length > 0 && (
        <div className="grid grid-cols-4 gap-4 mb-4">
          {totals.map((t, i) => (
            <div key={i} className="bg-white p-4 rounded shadow border-l-4 border-blue-500">
              <p className="text-xs text-gray-500">{t.label}</p>
              <p className="text-xl font-bold text-gray-900">{t.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* DATA TABLE */}
      <div className="bg-white p-4 rounded shadow">
        <h3 className="text-lg font-semibold mb-3">
          <i className={`fas ${currentReport?.icon} text-blue-500 mr-2`}></i>
          {currentReport?.label}
          {(reportKey === 'fee_collection' || reportKey === 'salary') && (
            <span className="text-sm font-normal text-gray-500 ml-2">
              — {MONTHS[month - 1]} {year}
            </span>
          )}
        </h3>
        {loading ? (
          <p className="text-gray-500">Loading...</p>
        ) : data.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <i className="fas fa-chart-bar text-4xl mb-3"></i>
            <p>No data available for this report.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  {columns.map(col => (
                    <th key={col} className="p-3 text-left font-semibold text-gray-700">
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((row, i) => (
                  <tr key={i} className="border-t hover:bg-gray-50">
                    {columns.map(col => (
                      <td key={col} className="p-3 text-gray-800">
                        {row[col] || '—'}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}